import axios from 'axios'
import React, { useEffect, useState } from 'react'
import jwtDecode from 'jwt-decode'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import DeviceCard from './DeviceCard'

dayjs.extend(relativeTime)

function DevicesSettings({ activeTab, isDarkMode }) {
	const [loading, setLoading] = useState(false)
	const [currentDevice, setCurrentDevice] = useState(null)
	const [websiteDevices, setWebsiteDevices] = useState([])
	const [extensionDevices, setExtensionDevices] = useState([])
	const [removingDevices, setRemovingDevices] = useState({})

	const fetchDevices = async () => {
		try {
			setLoading(true)
			if (activeTab !== 'devices') return

			const token = localStorage.getItem('jwtToken')
			const user = jwtDecode(token)

			// Fetch both website and extension devices separately
			const [websiteResponse, extensionResponse] = await Promise.all([
				axios.get('/api/user/devices'),
				axios.get('/api/user/devices/extension').catch(() => ({ data: [] })),
			])

			const websiteData = websiteResponse.data || []
			const extensionData = extensionResponse.data || []

			// Find current device from both types
			const allDevices = [...websiteData, ...extensionData]
			const current = allDevices.find(
				device => device?.deviceId === user?.deviceId
			)

			// Separate other devices by type (excluding current)
			const otherWebsiteDevices = websiteData
				.filter(device => device?.deviceId !== user?.deviceId)
				.sort((a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn))

			const otherExtensionDevices = extensionData
				.filter(device => device?.deviceId !== user?.deviceId)
				.sort((a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn))

			setCurrentDevice(current)
			setWebsiteDevices(otherWebsiteDevices)
			setExtensionDevices(otherExtensionDevices)
		} catch (error) {
			console.error('Error fetching devices:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchDevices()
	}, [activeTab])

	const handleRemoveDevice = async deviceId => {
		try {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: true }))
			await axios.post('/api/user/terminate-session', {
				deviceId: deviceId,
			})
			await fetchDevices()
		} catch (error) {
			console.error('Error removing device:', error)
		} finally {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: false }))
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center py-32">
				<div className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
					isDarkMode ? 'border-gray-400' : 'border-gray-500'
				}`} />
			</div>
		)
	}

	return (
		<div className="space-y-3 max-w-4xl mx-auto p-6">
			{currentDevice && (
				<div className="space-y-3">
					<DeviceCard
						device={currentDevice}
						isCurrent={true}
						isDarkMode={isDarkMode}
					/>
				</div>
			)}

			<div
				className={`border-t ${
					isDarkMode ? 'border-gray-700' : 'border-gray-200'
				}`}
			/>

			<div className="space-y-2">
				<h3
					className={`text-sm font-medium text-${
						isDarkMode ? 'white' : 'gray-900'
					}`}
				>
					Website Sessions ({websiteDevices.length})
				</h3>
				<div className="space-y-2">
					{websiteDevices.map(device => (
						<DeviceCard
							key={device._id}
							device={device}
							isCurrent={false}
							isDarkMode={isDarkMode}
							onRemove={() => handleRemoveDevice(device._id)}
							isRemoving={removingDevices[device._id]}
						/>
					))}
				</div>
			</div>

			<div
				className={`border-t ${
					isDarkMode ? 'border-gray-700' : 'border-gray-200'
				}`}
			/>

			<div className="space-y-2">
				<h3
					className={`text-sm font-medium text-${
						isDarkMode ? 'white' : 'gray-900'
					}`}
				>
					Extension Sessions ({extensionDevices.length})
				</h3>
				<div className="space-y-2">
					{extensionDevices.map(device => (
						<DeviceCard
							key={device._id}
							device={device}
							isCurrent={false}
							isDarkMode={isDarkMode}
							onRemove={() => handleRemoveDevice(device._id)}
							isRemoving={removingDevices[device._id]}
						/>
					))}
				</div>
			</div>
		</div>
	)
}

export default DevicesSettings
